import { useEffect, useState } from "react"
import { Card, Button, Form, Row, Col, Badge, Spinner, Table } from "react-bootstrap"
import { FaCalendarAlt, FaDollarSign, FaEdit, FaPlus, FaTrash, FaTimes, FaFileAlt, FaTags } from "react-icons/fa"
import { toast } from "react-toastify"
import moment from "moment"
import { getBaseUrl } from "../../../../Redux/Config/AppConfig"
import { useHotel } from "../../../../Common/HotelContext"

const CustomExpenseManager = () => {
  const baseUrl = getBaseUrl()

  // Use HotelContext
  const { selectedHotel } = useHotel()

  const [expenses, setExpenses] = useState([])
  const [form, setForm] = useState({
    date: "",
    amount: "",
    description: "",
    category: "",
  })
  const [editingId, setEditingId] = useState(null)
  const [loading, setLoading] = useState(false)

  // Fetch expenses on mount
  useEffect(() => {
    fetchExpenses()
  }, [selectedHotel])

  const fetchExpenses = async () => {
    setLoading(true)
    try {
      if (selectedHotel) {
        const token = localStorage.getItem("token")
        const res = await fetch(`${baseUrl}/expenses?hotel_id=${selectedHotel}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await res.json()
        setExpenses(data.data || [])
      } else {
        setExpenses([])
      }
    } catch (err) {
      toast.error("Failed to fetch expenses")
    }
    setLoading(false)
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const token = localStorage.getItem("token")
      if (editingId) {
        // PUT (Update)
        await fetch(`${baseUrl}/expenses/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        })
        setEditingId(null)
        toast.success("Expense updated successfully!")
      } else {
        // POST (Create)
        await fetch(`${baseUrl}/expenses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...form, hotel_id: selectedHotel }),
        })
        toast.success("Expense added successfully!")
      }
      setForm({ date: "", amount: "", description: "", category: "" })
      fetchExpenses()
    } catch (err) {
      toast.error("Failed to save expense")
    }
    setLoading(false)
  }

  const handleEdit = (expense) => {
    setEditingId(expense.id)
    setForm({
      date: expense.date,
      amount: expense.amount.toString(),
      description: expense.description,
      category: expense.category,
    })
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return

    setLoading(true)
    try {
      const token = localStorage.getItem("token")
      await fetch(`${baseUrl}/expenses/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      fetchExpenses()
      toast.success("Expense deleted successfully!")
    } catch (err) {
      toast.error("Failed to delete expense")
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setForm({ date: "", amount: "", description: "", category: "" })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY")
  }

  const getCategoryColor = (category) => {
    const colors = {
      "Food & Beverage": "primary",
      Maintenance: "warning",
      Marketing: "info",
      Utilities: "success",
      Staff: "secondary",
      Supplies: "dark",
      Other: "light",
    }
    return colors[category] || "outline-primary"
  }

  return (
    <div className="container-fluid px-4 py-4" style={{ maxWidth: "1200px" }}>
      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-primary mb-3">Custom Expense Management</h1>
        <p className="lead text-muted">Track and manage your hotel's custom expenses</p>
      </div>

      {/* Expense Form */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0 d-flex align-items-center">
            <FaPlus className="me-2" />
            {editingId ? "Edit Expense" : "Add New Expense"}
          </h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            {editingId ? "Update the expense details below" : "Add a new custom expense for your hotel"}
          </p>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaCalendarAlt className="me-2 text-primary" />
                    Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaDollarSign className="me-2 text-success" />
                    Amount (₹)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                    className="form-control-lg"
                  />
                  <Form.Text className="text-muted">Enter amount in Indian Rupees (INR)</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaFileAlt className="me-2 text-info" />
                    Description
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Enter expense description"
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className="d-flex align-items-center fw-semibold">
                    <FaTags className="me-2 text-warning" />
                    Category
                  </Form.Label>
                  <Form.Select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                    className="form-control-lg"
                  >
                    <option value="">Select Category</option>
                    <option value="Food & Beverage">Food & Beverage</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Staff">Staff</option>
                    <option value="Supplies">Supplies</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button type="submit" disabled={loading} variant="primary" size="lg" className="flex-fill">
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : editingId ? (
                  "Update Expense"
                ) : (
                  "Add Expense"
                )}
              </Button>
              {editingId && (
                <Button type="button" variant="outline-secondary" onClick={handleCancel} size="lg">
                  <FaTimes className="me-2" />
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Expense List */}
      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Expense Overview</h5>
          <small className="text-muted">All custom expenses for this hotel (amounts in ₹)</small>
        </Card.Header>
        <Card.Body>
          {loading && expenses.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Loading expenses...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-5">
              <FaDollarSign size={48} className="text-muted mb-3" />
              <h4 className="text-muted">No expenses found</h4>
              <p className="text-muted">Add your first expense to get started</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Amount (₹)</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-danger">
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="align-middle">
                      <td>
                        <Badge bg="dark" className="fs-6 p-2">
                          {formatDate(expense.date)}
                        </Badge>
                      </td>
                      <td>
                        <span className="h4 text-danger fw-bold">{formatCurrency(expense.amount)}</span>
                      </td>
                      <td>
                        <span className="text-dark">{expense.description}</span>
                      </td>
                      <td>
                        <Badge bg={getCategoryColor(expense.category)} className="p-2">
                          {expense.category}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(expense)}
                            disabled={loading}
                            className="d-flex align-items-center"
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(expense.id)}
                            disabled={loading}
                            className="d-flex align-items-center"
                          >
                            <FaTrash className="me-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default CustomExpenseManager
